// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useCallback, useMemo, useState, useEffect } from 'react';

import type { StrongExclude } from '@evolvemep/api-ts';

import { ChangeDataView } from 'components/Mantine/ChangeDataView/ChangeDataView';
import { useChangeDataView } from 'components/Mantine/ChangeDataView/useChangeDataView';
import { useFacilityOptional } from 'components/Mantine/GlobalFilters/FacilityRequired';
import { ActiveAllSelectFilter } from 'components/Mantine/GlobalFilters/Filters/ActiveAllSelectFilter';
import { AssigneesFilter } from 'components/Mantine/GlobalFilters/Filters/AssigneesFilter';
import { FacilitiesFilter } from 'components/Mantine/GlobalFilters/Filters/FacilitiesFilter';
import { ProjectsFilter } from 'components/Mantine/GlobalFilters/Filters/ProjectsFilter';
import { WorkCellsFilter } from 'components/Mantine/GlobalFilters/Filters/WorkCellsFilter';
import { WorkOrdersFilter } from 'components/Mantine/GlobalFilters/Filters/WorkOrdersFilter';
import { WorkOrderStatesFilter } from 'components/Mantine/GlobalFilters/Filters/WorkOrderStatesFilter';
import { FiltersPopover, FilterDivider } from 'components/Mantine/GlobalFilters/FiltersPopover';
import { GlobalFacilitySelect } from 'components/Mantine/GlobalFilters/GlobalFacilitySelect';
import { GlobalProjectSelect } from 'components/Mantine/GlobalFilters/GlobalProjectSelect';
import { useProjectOptional } from 'components/Mantine/GlobalFilters/ProjectRequired';
import { TimeFrameRequired } from 'components/Mantine/GlobalFilters/TimeFrameRequired';
import {
  type GlobalFilterValues,
  type GlobalFilterProps,
  globalFilterDefaults,
  type GlobalFilterKey,
  type GlobalFiltersConfig,
} from 'components/Mantine/GlobalFilters/types';
import { useUpdateGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import { isKeyOf } from 'helpers/isKeyOf';
import { isNotNil, isNil } from 'helpers/isNotNil';
import { strongIsEqual, strongEntries, strongKeys } from 'helpers/strongEntries';

const ComponentMap: {
  [K in StrongExclude<GlobalFilterKey, 'timeFrame'>]: React.FC<GlobalFilterProps<K>>;
} = {
  facilities: FacilitiesFilter,
  projects: ProjectsFilter,
  activeAll: ActiveAllSelectFilter,
  assignees: AssigneesFilter,
  workOrders: WorkOrdersFilter,
  workCells: WorkCellsFilter,
  workOrderStates: WorkOrderStatesFilter,
};

const ensureAllFiltersPresent = <T extends (GlobalFilterKey | '---')[]>(
  order: GlobalFilterKey extends T[number] ? T : never,
) => order;

const filterOrder = ensureAllFiltersPresent([
  'facilities',
  'projects',
  '---',
  'activeAll',
  'timeFrame',
  'assignees',
  'workOrderStates',
  'workOrders',
  'workCells',
]);

const WrappedFilter = <T extends GlobalFilterKey>({
  filterKey,
  filterConfig,
  internalFilters,
  facilityRequired,
  projectRequired,
  updateInternalFilter,
}: {
  filterKey: T;
  filterConfig: GlobalFiltersConfig;
  internalFilters: GlobalFilterValues;
  facilityRequired: boolean;
  projectRequired: boolean;
  updateInternalFilter: (key: T, value: GlobalFilterValues[T]) => void;
}) => {
  const defaultParams = useMemo((): GlobalFilterProps<GlobalFilterKey>['defaultParams'] => {
    const includeFacilities = filterConfig.facilities || facilityRequired;
    const includeProjects = filterConfig.projects || projectRequired;
    return {
      facilityIds: includeFacilities ? internalFilters.facilities.map((f) => f.facilityId) : undefined,
      projectIds: includeProjects ? internalFilters.projects.map((p) => p.projectId) : undefined,
    };
  }, [
    filterConfig.facilities,
    filterConfig.projects,
    facilityRequired,
    projectRequired,
    internalFilters.facilities,
    internalFilters.projects,
  ]);

  if (!isKeyOf(filterKey, filterConfig) || !isKeyOf(filterKey, ComponentMap)) return null;
  const Component = ComponentMap[filterKey] as React.FC<GlobalFilterProps<T>>;
  return (
    <Component
      filter={filterConfig[filterKey]}
      defaultParams={defaultParams}
      value={internalFilters[filterKey]}
      onChange={(value: GlobalFilterValues[T]) => updateInternalFilter(filterKey, value)}
    />
  );
};

export const GlobalFilters = () => {
  const { updateGlobalFilters, filterConfig, globalFilters } = useUpdateGlobalFilters();
  const [internalFilters, setInternalFilters] = useState(globalFilters);
  useEffect(() => setInternalFilters(globalFilters), [globalFilters]);

  const updateInternalFilter = useCallback(
    <T extends keyof GlobalFilterValues>(key: T, value: GlobalFilterValues[T]) =>
      setInternalFilters((f) => ({
        ...f,
        [key]: value,
      })),
    [],
  );

  const projectRequired = isNotNil(useProjectOptional());
  const facilityRequired = isNotNil(useFacilityOptional());

  const validFilters = useMemo(() => (isNil(filterConfig) ? [] : strongKeys(filterConfig)), [filterConfig]);

  const filtersAreSet = useMemo(
    () =>
      validFilters.some((key) =>
        key === 'timeFrame'
          ? false
          : key === 'activeAll'
            ? globalFilters[key].state !==
              (filterConfig?.activeAll?.defaultState ?? globalFilterDefaults.activeAll.state)
            : globalFilters[key].length > 0,
      ),
    [validFilters, globalFilters, filterConfig?.activeAll?.defaultState],
  );

  const hasChanges = useMemo(() => {
    const internal = Object.fromEntries(strongEntries(internalFilters).filter(([key]) => validFilters.includes(key)));
    const external = Object.fromEntries(strongEntries(globalFilters).filter(([key]) => validFilters.includes(key)));
    return !strongIsEqual(internal, external);
  }, [validFilters, globalFilters, internalFilters]);

  const hasDataViewContext = isNotNil(useChangeDataView());

  const toRender = useMemo(() => {
    const order = filterOrder.filter((key) => {
      if (key === '---') return true;
      if (key === 'facilities') {
        return !facilityRequired && validFilters.includes(key);
      }
      if (key === 'projects') {
        return !projectRequired && validFilters.includes(key);
      }
      return validFilters.includes(key);
    });
    if (order.every((key) => key === '---')) return [];
    return order;
  }, [facilityRequired, projectRequired, validFilters]);

  return (
    <>
      {facilityRequired && <GlobalFacilitySelect />}
      {projectRequired && <GlobalProjectSelect />}

      {validFilters.includes('timeFrame') && (
        // Time Frame is a required filter (value must always exist),
        // so it is rendered outside of the FiltersPopover.
        <TimeFrameRequired />
      )}

      {(hasDataViewContext || toRender.length > 0) && (
        <FiltersPopover
          hasChanges={hasChanges}
          filtersAreSet={filtersAreSet}
          onApplyChanges={() => updateGlobalFilters(structuredClone(internalFilters))}
          onDiscardChanges={() => setInternalFilters(globalFilters)}
        >
          {hasDataViewContext && (
            <>
              <ChangeDataView />
              <FilterDivider />
            </>
          )}

          {isNotNil(filterConfig) &&
            toRender.map((key, i) =>
              key === '---' ? (
                isNotNil(toRender[i - 1]) &&
                toRender[i - 1] !== '---' &&
                i < toRender.length - 1 && (
                  // oxlint-disable-next-line react/no-array-index-key
                  <FilterDivider key={`divider-${i}`} />
                )
              ) : (
                <WrappedFilter
                  key={key}
                  filterKey={key}
                  internalFilters={internalFilters}
                  facilityRequired={facilityRequired}
                  projectRequired={projectRequired}
                  filterConfig={filterConfig}
                  updateInternalFilter={updateInternalFilter}
                />
              ),
            )}
        </FiltersPopover>
      )}
    </>
  );
};
